import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, User, Calendar, Tag, Share2, Home } from 'lucide-react';
import CustomCursor from './CustomCursor';
import articlesData from './articles.json';

const ArticlePage = () => {
    const navigate = useNavigate();
    const { slug } = useParams();

    const article = articlesData.find(a => a.slug === slug);

    useEffect(() => {
        if (article) {
            // SEO
            document.title = article.seo.metaTitle;
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
                metaDescription.setAttribute('content', article.seo.metaDescription);
            }
        }
    }, [article]);

    if (!article) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="text-center">
                    <div className="text-6xl mb-4">😕</div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-4">Article introuvable</h2>
                    <button
                        onClick={() => navigate('/blog')}
                        className="px-6 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition"
                    >
                        Retour au Blog
                    </button>
                </div>
            </div>
        );
    }

    // Convertir le markdown en HTML basique
    const renderContent = (content) => {
        const lines = content.split('\n');
        return lines.map((line, i) => {
            // Headers
            if (line.startsWith('### ')) {
                return <h3 key={i} className="text-2xl font-black text-gray-900 mt-8 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{line.replace('### ', '')}</h3>;
            }
            if (line.startsWith('## ')) {
                return <h2 key={i} className="text-3xl font-black text-red-600 mt-12 mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>{line.replace('## ', '')}</h2>;
            }
            if (line.startsWith('# ')) {
                return <h1 key={i} className="text-4xl font-black text-gray-900 mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>{line.replace('# ', '')}</h1>;
            }

            // Bold text
            if (line.includes('**')) {
                const parts = line.split('**');
                return (
                    <p key={i} className="text-lg text-gray-700 leading-relaxed mb-4">
                        {parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="font-black text-gray-900">{part}</strong> : part)}
                    </p>
                );
            }

            // Lists
            if (line.startsWith('- ')) {
                return <li key={i} className="text-lg text-gray-700 ml-6 mb-2 list-disc">{line.replace('- ', '')}</li>;
            }

            // Separator
            if (line === '---') {
                return <hr key={i} className="my-12 border-t-2 border-gray-200" />;
            }

            // Italic (info)
            if (line.startsWith('*') && line.endsWith('*')) {
                return <p key={i} className="text-gray-500 italic text-sm my-4">{line.replace(/\*/g, '')}</p>;
            }

            // Empty line
            if (line.trim() === '') {
                return <div key={i} className="h-4"></div>;
            }

            // Regular paragraph
            return <p key={i} className="text-lg text-gray-700 leading-relaxed mb-4">{line}</p>;
        });
    };

    return (
        <div className="relative min-h-screen bg-white overflow-hidden">
            <CustomCursor />

            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Nunito:wght@400;600;700;800&display=swap');
      `}</style>

            {/* Navigation */}
            <motion.nav
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b-4 border-red-600 shadow-xl"
            >
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <motion.a
                            href="/blog"
                            className="flex items-center gap-2 px-6 py-3 rounded-full bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <ArrowLeft className="w-5 h-5" />
                            Retour Blog
                        </motion.a>

                        <motion.a
                            href="/"
                            className="flex items-center gap-2 px-5 py-3 rounded-full bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Home className="w-5 h-5" />
                        </motion.a>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="relative w-10 h-10">
                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-600 to-red-700 shadow-2xl"></div>
                            <div className="absolute inset-0 top-0 h-1/2 rounded-t-full bg-red-600"></div>
                            <div className="absolute inset-0 bottom-0 top-1/2 h-1/2 rounded-b-full bg-white"></div>
                            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-900 transform -translate-y-1/2"></div>
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border border-gray-900"></div>
                        </div>
                        <div>
                            <div className="text-3xl font-black text-red-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                PokeMovie
                            </div>
                            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider">Serveur Cobblemon</div>
                        </div>
                    </div>
                </div>
            </motion.nav>

            {/* Article Header */}
            <section className="relative pt-32 pb-12 px-6 bg-gradient-to-br from-red-600 via-red-700 to-red-800">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-full h-full"
                         style={{
                             backgroundImage: `url(${article.image})`,
                             backgroundSize: 'cover',
                             backgroundPosition: 'center'
                         }}
                    ></div>
                </div>

                <div className="max-w-4xl mx-auto relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        {/* Category Badge */}
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: "spring" }}
                            className="inline-block mb-6"
                        >
                          <span className="px-5 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white font-bold text-sm border-2 border-white/30">
                            {article.category}
                          </span>
                        </motion.div>

                        <h1 className="text-4xl lg:text-6xl font-black text-white mb-6 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            {article.title}
                        </h1>

                        <p className="text-xl text-red-100 mb-8 font-semibold leading-relaxed">
                            {article.excerpt}
                        </p>

                        {/* Meta Info */}
                        <div className="flex flex-wrap items-center gap-6 text-white/90 font-semibold">
                            <div className="flex items-center gap-2">
                                <User className="w-5 h-5" />
                                <span>{article.author}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5" />
                                <span>{new Date(article.publishedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5" />
                                <span>{article.readTime} min de lecture</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Article Image */}
            <section className="px-6 -mt-20 relative z-20">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                        className="rounded-3xl overflow-hidden shadow-2xl border-8 border-white"
                    >
                        <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-96 object-cover"
                        />
                    </motion.div>
                </div>
            </section>

            {/* Article Content */}
            <article className="py-16 px-6">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.8 }}
                        className="prose prose-lg max-w-none"
                    >
                        {renderContent(article.content)}
                    </motion.div>

                    {/* Tags */}
                    <div className="mt-16 pt-8 border-t-2 border-gray-200">
                        <div className="flex items-center gap-3 mb-4">
                            <Tag className="w-5 h-5 text-gray-500" />
                            <span className="font-bold text-gray-700">Tags :</span>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {article.tags.map(tag => (
                                <span key={tag} className="px-4 py-2 rounded-full bg-red-50 text-red-600 font-bold text-sm border-2 border-red-100">
                                  #{tag}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Share */}
                    <div className="mt-12 p-8 rounded-3xl bg-gradient-to-br from-red-50 to-orange-50 border-4 border-red-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-2xl font-black text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                    Article utile ?
                                </h3>
                                <p className="text-gray-600 font-semibold">Partage-le avec tes amis !</p>
                            </div>
                            <motion.button
                                className="flex items-center gap-3 px-6 py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 transition-all"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Share2 className="w-5 h-5" />
                                Partager
                            </motion.button>
                        </div>
                    </div>

                    {/* Back to Blog */}
                    <div className="mt-16 text-center">
                        <motion.button
                            onClick={() => navigate('/blog')}
                            className="inline-flex items-center gap-3 px-10 py-5 rounded-2xl bg-gray-900 text-white font-black text-xl hover:bg-gray-800 transition-all shadow-2xl"
                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                            whileHover={{ scale: 1.05, y: -3 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <ArrowLeft className="w-6 h-6" />
                            Retour aux Articles
                        </motion.button>
                    </div>
                </div>
            </article>
        </div>
    );
};

export default ArticlePage;